import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, BorderStyle } from 'docx';
import { saveAs } from 'file-saver';

interface SurveyTemplate {
  id: string;
  name: string;
  description: string;
  template_type: string;
  questions: any[];
  created_at: string;
}

export const exportTemplateToWord = async (template: SurveyTemplate) => {
  try {
    // Create a new document
    const doc = new Document({
      creator: "Edugogy Survey System",
      title: template.name,
      description: "Professional development survey template",
      styles: {
        default: {
          heading1: {
            run: {
              size: 28,
              bold: true,
              color: "1f2937", // gray-800
              font: "Calibri"
            },
            paragraph: {
              spacing: {
                after: 240,
              },
            },
          },
          heading2: {
            run: {
              size: 24,
              bold: true,
              color: "374151", // gray-700
              font: "Calibri"
            },
            paragraph: {
              spacing: {
                before: 240,
                after: 120,
              },
            },
          },
          heading3: {
            run: {
              size: 20,
              bold: true,
              color: "4b5563", // gray-600
              font: "Calibri"
            },
            paragraph: {
              spacing: {
                before: 120,
                after: 120,
              },
            },
          },
        },
      },
      sections: [
        {
          children: [
            // Header with brand styling
            new Paragraph({
              children: [
                new TextRun({
                  text: "EDUGOGY",
                  bold: true,
                  size: 32,
                  color: "2563eb", // blue-600 (brand color)
                  font: "Calibri"
                }),
                new TextRun({
                  text: " Professional Development",
                  size: 24,
                  color: "4b5563",
                  font: "Calibri"
                }),
              ],
              alignment: AlignmentType.CENTER,
              spacing: {
                after: 480,
              },
            }),

            // Document title
            new Paragraph({
              children: [
                new TextRun({
                  text: template.name,
                  bold: true,
                  size: 28,
                  color: "1f2937",
                  font: "Calibri"
                }),
              ],
              heading: HeadingLevel.HEADING_1,
              alignment: AlignmentType.LEFT,
            }),

            // Template info
            new Paragraph({
              children: [
                new TextRun({
                  text: `Template Type: `,
                  bold: true,
                  size: 22,
                  font: "Calibri"
                }),
                new TextRun({
                  text: template.template_type.replace('_', ' ').toUpperCase(),
                  size: 22,
                  color: "2563eb",
                  font: "Calibri"
                }),
              ],
              spacing: {
                after: 120,
              },
            }),

            // Creation date
            new Paragraph({
              children: [
                new TextRun({
                  text: `Created: `,
                  bold: true,
                  size: 22,
                  font: "Calibri"
                }),
                new TextRun({
                  text: new Date(template.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }),
                  size: 22,
                  font: "Calibri"
                }),
              ],
              spacing: {
                after: 240,
              },
            }),

            // Description section
            new Paragraph({
              children: [
                new TextRun({
                  text: "Description",
                  bold: true,
                  size: 24,
                  color: "374151",
                  font: "Calibri"
                }),
              ],
              heading: HeadingLevel.HEADING_2,
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: template.description || "No description provided.",
                  size: 22,
                  font: "Calibri"
                }),
              ],
              spacing: {
                after: 240,
              },
            }),

            // Questions section
            new Paragraph({
              children: [
                new TextRun({
                  text: "Survey Questions",
                  bold: true,
                  size: 24,
                  color: "374151",
                  font: "Calibri"
                }),
              ],
              heading: HeadingLevel.HEADING_2,
            }),

            // Generate question paragraphs
            ...template.questions.map((question, index) => [
              new Paragraph({
                children: [
                  new TextRun({
                    text: `Question ${index + 1}: `,
                    bold: true,
                    size: 22,
                    color: "4b5563",
                    font: "Calibri"
                  }),
                  new TextRun({
                    text: question.question,
                    size: 22,
                    font: "Calibri"
                  }),
                ],
                spacing: {
                  before: 120,
                  after: 60,
                },
              }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: `Type: ${question.type} • Required: ${question.required ? 'Yes' : 'No'}`,
                    size: 20,
                    italics: true,
                    color: "6b7280",
                    font: "Calibri"
                  }),
                ],
                spacing: {
                  after: 120,
                },
              }),
            ]).flat(),

            // Footer
            new Paragraph({
              children: [
                new TextRun({
                  text: "Generated by Edugogy Professional Development System",
                  size: 18,
                  italics: true,
                  color: "9ca3af",
                  font: "Calibri"
                }),
              ],
              alignment: AlignmentType.CENTER,
              spacing: {
                before: 480,
              },
            }),
          ],
        },
      ],
    });

    // Generate and download the document
    const blob = await Packer.toBlob(doc);
    const fileName = `${template.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_template.docx`;
    saveAs(blob, fileName);

    return true;
  } catch (error) {
    console.error('Error exporting to Word:', error);
    throw error;
  }
};